import { ICostTotals, PrefillableCosts, PrefillVariation } from "./types";

import { IWorker } from "@/hooks/chime";
import { IInternalPlantItem } from "@/hooks/plantItems";
import { ISubcontractor } from "@/hooks/useGetSubcontractors";
import { IPlantCostEntry } from "@/pages/ForecastingPage";
import { IBudgetForecasts, IPlantCost, IVariation } from "@/types/forecasting";
import { DEFAULT_HOURS_PER_DAY, getLabourAmount } from "@/utils/labour";
import {
  getPlantAmount,
  getPlantCategories,
  getPlantCategoryName
} from "@/utils/plant";

/**
 * Plant is forecast per category. Forecasts saved before that hold one row
 * per plant item (`Id` = item no); those fold into their category, summing
 * quantities and keeping the longest duration. Items without a category
 * can't be forecast, so they are dropped.
 */
const toPrefillPlantCosts = (
  saved: IPlantCost[] = [],
  plantItems: IInternalPlantItem[]
): IPlantCostEntry[] => {
  const categories = getPlantCategories(plantItems);
  const entries = new Map<string, IPlantCostEntry>();

  saved.forEach((p) => {
    const legacyItem = plantItems.find((item) => item.no === p.Id);
    const name = legacyItem ? getPlantCategoryName(legacyItem) : p.Name;
    if (!name) return;
    const category = categories.get(name);
    const existing = entries.get(name);
    const entry = {
      id: name,
      name,
      unitOfMeasure: category?.unitOfMeasure ?? p.UnitOfMeasure,
      unitCost: category?.unitCost ?? p.UnitCost,
      quantity: (existing?.quantity ?? 0) + p.Quantity,
      noOfDays: Math.max(existing?.noOfDays ?? 0, p.NoOfDays)
    };
    entries.set(name, { ...entry, amount: getPlantAmount(entry) });
  });

  return [...entries.values()];
};

/**
 * Maps a saved forecast's budget onto the cost-step entries. Labour rates and
 * plant category unit costs are refreshed from the live lookups so a copied
 * entry is priced at today's rate; the saved values are the fallback for anything that
 * has since disappeared from those lookups.
 */
export const toPrefillableCosts = (
  budget: IBudgetForecasts,
  workers: IWorker[],
  plantItems: IInternalPlantItem[],
  subcontractors: ISubcontractor[]
): PrefillableCosts => ({
  labourCosts:
    budget.BudgetLabourCosts?.map((l) => {
      const worker = workers.find((w) => w.id === l.Id);
      const entry = {
        ratePerHour: worker?.hourlyRate ?? l.RatePerHour,
        hoursPerDay: l.HoursPerDay ?? DEFAULT_HOURS_PER_DAY,
        noOfDays: l.NoOfDays
      };
      return {
        ...entry,
        id: l.Id,
        name: l.Name,
        labourTrade: worker?.trade?.trim() || "",
        amount: getLabourAmount(entry)
      };
    }) ?? [],

  plantCosts: toPrefillPlantCosts(budget.BudgetPlantCosts, plantItems),

  materialCosts: budget.BudgetMaterialCost
    ? [{ amount: parseFloat(budget.BudgetMaterialCost.Amount.toFixed(2)) }]
    : [],

  subcontractorCosts:
    budget.BudgetSubContractorCost?.map((s) => ({
      name: s.Name,
      // Only the subcontractor is saved; its trade is looked up again.
      tradeCode: subcontractors.find((sc) => sc.no === s.Name)?.tradeCode ?? "",
      amount: s.Amount
    })) ?? []
});

export const toPrefillVariations = (
  variations: IVariation[] = []
): PrefillVariation[] =>
  variations.map((v, i) => ({
    id: `prev_${i}`,
    variation: v.Variation,
    utilizedAmount: v.UtilizedAmount,
    availableAmount: v.AvailableAmount,
    totalAmount: v.TotalAmount
  }));

const sumAmounts = (entries: { Amount: number }[] = []) =>
  entries.reduce((sum, entry) => sum + entry.Amount, 0);

export const getCostTotals = (budget?: IBudgetForecasts): ICostTotals => ({
  labour: sumAmounts(budget?.BudgetLabourCosts),
  plant: sumAmounts(budget?.BudgetPlantCosts),
  material: budget?.BudgetMaterialCost?.Amount ?? 0,
  subcontractor: sumAmounts(budget?.BudgetSubContractorCost)
});

export const hasAnyCost = (totals: ICostTotals) =>
  Object.values(totals).some((amount) => amount > 0);
